import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Wrapper from "../../helpers/Wrapper";
import FilterIcon from "../../../assets/icons/FilterIcon";
import Button from "../Button";
import SearchIcon from "../../../assets/icons/SearchIcon";
import "./globalFilter.scss";
import Spinner from "../../../assets/icons/Spinner";
import { ReactComponent as Collapsed } from "../../../assets/icons/icons-svg/Collapsed-icon.svg";
import { ReactComponent as Expand } from "../../../assets/icons/icons-svg/Expand-icon.svg";
import {
    resetFilter,
    setSelectedFilters,
    setInitialData,
} from "../../../store/globalFilterSlice";
import useFetch from "../../../hooks/useFetch";
import ConfirmationModal from "../../../components/ui/alert/ConfirmationModal"; // <-- Add this import

const sectionNames = [
    "country",
    "store_type",
    // "category",
    // "sub_category",
    "segment",
    // "form",
    "manufacture",
    "brand",
    // "variant",
    "pack_size",
    // "promotion",
    "sku",
];

const filterKeyMap = {
    country: "country_id",
    store_type: "store_type_id",
    // category: "category_id",
    // sub_category: "sub_category_id",
    segment: "segment_id",
    // form: "form_id",
    manufacture: "manufacture_id",
    brand: "brand_id",
    // variant: "variant_id",
    pack_size: "pack_size_id",
    // promotion: "promotion_id",
    sku: "sku_id",
};

const getDisplayName = (display_name) => {
    const names = {
        country: "Country",
        store_type: "Store Type",
        // category: "Category",
        // sub_category: "Sub Category",
        segment: "Segment",
        // form: "Form",
        manufacture: "Manufacture",
        brand: "Brand",
        // variant: "Variant",
        pack_size: "Pack Size",
        // promotion: "Promotion",
        sku: "SKU",
    };
    return names[display_name] || display_name;
};

const getPlural = (name) => {
    if (name.toLowerCase() === "country") return "countries";
    if (name.toLowerCase() === "category") return "categories";
    if (name.toLowerCase() === "store type") return "store types";
    return name.toLowerCase() + "s";
};

const ITEMS_TO_SHOW = 6;
const ALPHABETICAL_THRESHOLD = 15;
const CHUNK_SIZE = 5;

const groupByFirstLetter = (items) => {
    const grouped = {};
    items.forEach((item) => {
        const firstChar = String(item.name).charAt(0).toUpperCase();
        const key = /[A-Z]/.test(firstChar) ? firstChar : "#";
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(item);
    });
    return grouped;
};

const getAvailableLetters = (items) => {
    const letters = new Set();
    items.forEach((item) => {
        const firstChar = String(item.name).charAt(0).toUpperCase();
        const key = /[A-Z]/.test(firstChar) ? firstChar : "#";
        letters.add(key);
    });
    return Array.from(letters).sort((a, b) => {
        if (a === "#") return -1;
        if (b === "#") return 1;
        return a.localeCompare(b);
    });
};

const chunkArray = (arr, size) => {
    const result = [];
    for (let i = 0; i < arr.length; i += size) {
        result.push(arr.slice(i, i + size));
    }
    return result;
};

const CrossGlobalFilter = () => {
    const dispatch = useDispatch();
    const { crossSelectedFilters = {}, filterData } = useSelector((state) => state.globalFilter);
    const [tempSelectedFilters, setTempSelectedFilters] = useState(crossSelectedFilters);

    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    const { post, isLoading, error } = useFetch();
    const postRef = useRef(post);
    useEffect(() => {
        postRef.current = post;
    }, [post]);

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [openDrawer, setOpenDrawer] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [expandedSections, setExpandedSections] = useState({});
    const [selectedLetters, setSelectedLetters] = useState({});
    const letterRefs = useRef({});

    const [loadingSection, setLoadingSection] = useState(null);
    const [dirtySection, setDirtySection] = useState(null);

    // Modal state for error
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorModalMsg, setErrorModalMsg] = useState("");

    // Modal state for Clear All
    const [clearAllModalOpen, setClearAllModalOpen] = useState(false);

    // Collapse all sections by default
    useEffect(() => {
        const expanded = {};
        sectionNames.forEach((key) => {
            expanded[key] = false; // collapsed by default!
        });
        setExpandedSections(expanded);
    }, []);

    const isInitialLoading = isLoading && Object.keys(filterData).length === 0;

    useEffect(() => {
        async function fetchFilterData() {
            const payload = {
                filter_input: {
                    date_filter: {
                        mat_ty_start_date: startDate,
                        mat_ty_end_date: endDate,
                        mat_ly_start_date: prevStartDate,
                        mat_ly_end_date: prevEndDate,
                        mat_2y_start_date: secondPrevStartDate,
                        mat_2y_end_date: secondPrevEndDate,
                    },
                    section_name: sectionNames,
                },
            };
            const response = await postRef.current("/ri/cross-source-global-filter", payload);
            if (response) {
                dispatch(setInitialData(response));
            }
        }
        fetchFilterData();
    }, [startDate, endDate, prevStartDate, prevEndDate, secondPrevStartDate, secondPrevEndDate, dispatch]);

    const handleCloseDrawer = async (key) => {
        setOpenDrawer(null);
        setExpandedSections((prev) => ({ ...prev, [key]: false }));
        setSelectedLetters((prev) => ({ ...prev, [key]: null }));

        if (dirtySection === key) {
            const filterForApi = {};
            for (const [filterKey, values] of Object.entries(tempSelectedFilters)) {
                if (values && values.length > 0) {
                    const apiKey = filterKeyMap[filterKey];
                    if (apiKey) filterForApi[apiKey] = values;
                }
            }

            setLoadingSection(key);

            // Remove any section that has a filter applied
            const filteredSections = sectionNames.filter(
                section => !filterForApi[filterKeyMap[section]]
            );

            const payload = {
                filter_input: {
                    date_filter: {
                        mat_ty_start_date: startDate,
                        mat_ty_end_date: endDate,
                        mat_ly_start_date: prevStartDate,
                        mat_ly_end_date: prevEndDate,
                        mat_2y_start_date: secondPrevStartDate,
                        mat_2y_end_date: secondPrevEndDate,
                    },
                    filter: filterForApi,
                    section_name: filteredSections,
                },
            };
            const response = await postRef.current("/ri/cross-source-global-filter", payload);
            if (response) {
                dispatch(setInitialData({ ...filterData, ...response }));
            }
            setLoadingSection(null);
            setDirtySection(null);
        }
    };

    const handleToggleDrawer = async (key) => {
        if (openDrawer && openDrawer !== key && dirtySection === openDrawer) {
            const filterForApi = {};
            for (const [filterKey, values] of Object.entries(tempSelectedFilters)) {
                if (values && values.length > 0) {
                    const apiKey = filterKeyMap[filterKey];
                    if (apiKey) filterForApi[apiKey] = values;
                }
            }

            setLoadingSection(openDrawer);

            // Remove any section that has a filter applied
            const filteredSections = sectionNames.filter(
                section => !filterForApi[filterKeyMap[section]]
            );

            const payload = {
                filter_input: {
                    date_filter: {
                        mat_ty_start_date: startDate,
                        mat_ty_end_date: endDate,
                        mat_ly_start_date: prevStartDate,
                        mat_ly_end_date: prevEndDate,
                        mat_2y_start_date: secondPrevStartDate,
                        mat_2y_end_date: secondPrevEndDate,
                    },
                    filter: filterForApi,
                    section_name: filteredSections,
                },
            };
            const response = await postRef.current("/ri/cross-source-global-filter", payload);
            if (response) {
                dispatch(setInitialData({ ...filterData, ...response }));
            }
            setLoadingSection(null);
            setDirtySection(null);
        }

        if (openDrawer === key) {
            await handleCloseDrawer(key);
        } else {
            setOpenDrawer(key);
            setExpandedSections((prev) => ({ ...prev, [key]: false }));
            setSelectedLetters((prev) => ({ ...prev, [key]: null }));
        }
    };

    // Show more expands, show less collapses
    const handleShowMore = (key) => {
        setExpandedSections((prev) => ({ ...prev, [key]: true }));
    };
    const handleShowLess = (key) => {
        setExpandedSections((prev) => ({ ...prev, [key]: false }));
        setSelectedLetters((prev) => ({ ...prev, [key]: null }));
    };

    const handleLetterClick = (key, letter) => {
        setSelectedLetters((prev) => ({ ...prev, [key]: letter }));
    };

    // Select All only for visible (filtered) items
    const handleSelectAll = (category, items) => {
        setTempSelectedFilters((prev) => {
            // Only select items that are currently visible (filtered by search)
            const visibleValues = items
                .filter(({ is_active }) => is_active !== false)
                .map(({ value }) => value);

            const current = prev[category] || [];
            const isAllSelected =
                visibleValues.length > 0 &&
                visibleValues.every(val => current.includes(val));

            let updatedSelected;
            if (isAllSelected) {
                // Remove only the visible values from selection
                updatedSelected = current.filter(val => !visibleValues.includes(val));
            } else {
                // Add all visible values to selection (avoid duplicates)
                updatedSelected = Array.from(new Set([...current, ...visibleValues]));
            }

            return updatedSelected.length > 0
                ? { ...prev, [category]: updatedSelected }
                : Object.fromEntries(Object.entries(prev).filter(([k]) => k !== category));
        });
        setDirtySection(category);
    };

    const handleCheckboxChange = (category, value) => {
        setTempSelectedFilters((prev) => {
            const prevSelected = prev[category] || [];
            const updatedSelected = prevSelected.includes(value)
                ? prevSelected.filter((item) => item !== value)
                : [...prevSelected, value];
            return updatedSelected.length > 0
                ? { ...prev, [category]: updatedSelected }
                : Object.fromEntries(Object.entries(prev).filter(([k]) => k !== category));
        });
        setDirtySection(category);
    };

    const handleClearFilters = () => {
        setClearAllModalOpen(true);
    };

    const confirmClearAll = () => {
        dispatch(resetFilter({ filterType: 'cross' }));
        setTempSelectedFilters({});
        setClearAllModalOpen(false);
    };

    const cancelClearAll = () => {
        setClearAllModalOpen(false);
    };

    // --- Modified handleApplyFilters with validation ---
    const handleApplyFilters = async () => {
        // Build payload for validation API
        const filterForApi = {};
        for (const [filterKey, values] of Object.entries(tempSelectedFilters)) {
            if (values && values.length > 0) {
                const apiKey = filterKeyMap[filterKey];
                if (apiKey) filterForApi[apiKey] = values;
            }
        }
        const payload = {
            global_filter: {
                date_filter: {
                    mat_ty_start_date: startDate,
                    mat_ty_end_date: endDate,
                    mat_ly_start_date: prevStartDate,
                    mat_ly_end_date: prevEndDate,
                    mat_2y_start_date: secondPrevStartDate,
                    mat_2y_end_date: secondPrevEndDate,
                },
                filter: filterForApi,
            }
        };

        try {
            const response = await postRef.current("ri/cross-source-data-validate", payload);
            if (response?.valid) {
                dispatch(setSelectedFilters({ filters: tempSelectedFilters, filterType: 'cross' }));
                setIsFilterOpen(false);
            } else {
                setErrorModalMsg(response?.message || "Data is not available for the selected combination of filters.");
                setErrorModalOpen(true);
            }
        } catch (e) {
            setErrorModalMsg("Something went wrong. Please try again.");
            setErrorModalOpen(true);
        }
    };

    if (error) {
        return (
            <Wrapper>
                <div className="globalFilter_container active">
                    <div>Error loading filters. Please try again.</div>
                </div>
            </Wrapper>
        );
    }
    const renderLetterNav = (groupedItems, key) => {
        const fullAlphabet = ["#", ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))];
        const availableLetters = Object.keys(groupedItems);

        return (
            <div className="alphabet_nav alpha-bar-horizontal">
                {fullAlphabet.map((letter) => {
                    const isAvailable = availableLetters.includes(letter);
                    const isSelected = selectedLetters[key] === letter;
                    return (
                        <button
                            key={letter}
                            className={`alphabet_btn${isAvailable ? " available" : " unavailable"}${isSelected ? " selected" : ""}`}
                            onClick={() => {
                                setSelectedLetters((prev) => ({ ...prev, [key]: letter }));
                                setTimeout(() => {
                                    if (letterRefs.current[letter]) {
                                        letterRefs.current[letter].scrollIntoView({
                                            behavior: "smooth",
                                            block: "start"
                                        });
                                    }
                                }, 0);
                            }}
                            disabled={!isAvailable}
                        >
                            {letter}
                        </button>
                    );
                })}
            </div>
        );
    };

    return (
        <Wrapper>
            <div className="multistep_dropdown_container">
                <div className="dropdown_wrapper_btn">
                    {/* <Button
                        icon={<FilterIcon className="btn_icon" />}
                        className={isFilterOpen ? "secondary_btn active" : "secondary_btn"}
                        type="button"
                        handleClick={() => {
                            setIsFilterOpen((prev) => !prev);
                            if (!isFilterOpen) setTempSelectedFilters(crossSelectedFilters);
                        }}
                    /> */}
                    <Button
                        icon={<FilterIcon className="btn_icon" />}
                        className={isFilterOpen ? "secondary_btn active" : "secondary_btn"}
                        type="button"
                        handleClick={() => {
                            setIsFilterOpen((prev) => {
                                const next = !prev;
                                if (next) {
                                    setTempSelectedFilters(crossSelectedFilters);
                                    setOpenDrawer("country"); // Open the first section
                                    setExpandedSections((prevSections) => ({
                                        ...prevSections,
                                        country: false // Collapsed = vertical view
                                    }));
                                }
                                return next;
                            });
                        }}
                    />
                </div>
            </div>

            <div className={`globalFilter_container${isFilterOpen ? " active" : ""}`}>
                {/* Header */}
                <div className="filter_header">
                    <h2 className="filter_title">Cross Filters</h2>
                    <button
                        className="close_btn"
                        onClick={() => {
                            setIsFilterOpen(false);
                            setTempSelectedFilters(crossSelectedFilters);
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Search Input */}
                {/* <div className="searchbar_container">
                    <input
                        type="text"
                        placeholder="Search"
                        className="search_input"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                    {searchText && (
                        <button
                            className="search_clear_btn"
                            onClick={() => setSearchText("")}
                            type="button"
                            aria-label="Clear search"
                        >
                            ×
                        </button>
                    )}
                    <span className="search_icon_right">
                        <SearchIcon />
                    </span>
                </div> */}
                <div className="searchbar_container">
                    <input
                        type="text"
                        placeholder="Search..."
                        className="search_input"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                    <span className="search_icon_right">
                        <SearchIcon />
                    </span>
                    {searchText && (
                        <button
                            className="search_clear_btn"
                            onClick={() => setSearchText("")}
                            type="button"
                            aria-label="Clear search"
                        >
                            ×
                        </button>
                    )}
                </div>

                {/* Filter Sections */}
                <div className="filter_sections">
                    {sectionNames.map((key) => {
                        const sectionIsLoading =
                            (isInitialLoading && !filterData[key]) ||
                            loadingSection === key;

                        const filterObj = filterData[key] || {};
                        const data = filterObj.data || [];
                        const filteredData = data.filter(({ name }) =>
                            typeof name === "string" &&
                            name.toLowerCase().includes(searchText.toLowerCase())
                        );
                        const hasSearch = searchText.trim().length > 0;
                        const hasResults = filteredData.length > 0;
                        const selectedCount = tempSelectedFilters[key]?.length || 0;
                        const totalCount = data.length;
                        const displayCategoryName = getDisplayName(
                            filterObj.display_name || key
                        );

                        const isExpanded = expandedSections[key];
                        const shouldShowAlphabetical =
                            filteredData.length > ALPHABETICAL_THRESHOLD && !searchText;

                        const hasMoreThanSix = filteredData.length > ITEMS_TO_SHOW;
                        const shouldShowMoreLink = hasMoreThanSix && isExpanded === false;
                        const shouldShowLessLink = hasMoreThanSix && isExpanded === true;

                        let itemsToDisplay = [];
                        let groupedItems = {};
                        let availableLetters = [];

                        // Always show first ITEMS_TO_SHOW in single column when collapsed, even for alphabetical
                        if (shouldShowAlphabetical && isExpanded) {
                            groupedItems = groupByFirstLetter(filteredData);
                            availableLetters = getAvailableLetters(filteredData);
                        } else if (isExpanded) {
                            itemsToDisplay = filteredData;
                        } else {
                            itemsToDisplay = filteredData.slice(0, ITEMS_TO_SHOW);
                        }

                        let leftCol = [],
                            rightCol = [];
                        if (isExpanded && !shouldShowAlphabetical) {
                            itemsToDisplay.forEach((item, idx) => {
                                (idx % 2 === 0 ? leftCol : rightCol).push(item);
                            });
                        }

                        // Helper for select all checked state (only for visible/filtered items)
                        const visibleValues = filteredData
                            .filter(({ is_active }) => is_active !== false)
                            .map(({ value }) => value);
                        const isAllVisibleSelected =
                            visibleValues.length > 0 &&
                            visibleValues.every(val => (tempSelectedFilters[key] || []).includes(val));

                        return (
                            <div key={key} className="filter_section">
                                <div
                                    className={`section_header${hasSearch && !hasResults ? " section_disabled" : ""}`}
                                    onClick={() => {
                                        if (hasSearch && !hasResults || sectionIsLoading) return;
                                        handleToggleDrawer(key);
                                    }}
                                    style={
                                        hasSearch && !hasResults ? { pointerEvents: "none" } : {}
                                    }
                                >
                                    <div className="section_title">
                                        <span className="title_main">
                                            {displayCategoryName}
                                            {selectedCount > 0 ? (
                                                // <span style={{ marginLeft: 8, color: "#4B5563", fontWeight: 400, fontSize: "0.95em" }}>
                                                <span style={{ marginLeft: 8, color: "#213345", fontWeight: 600, fontSize: "1rem" }}>

                                                    {`${selectedCount}/${totalCount} Selected`}
                                                </span>
                                            ) : null}
                                        </span>
                                    </div>
                                    {sectionIsLoading && (
                                        <span className="section_loader">
                                            <Spinner size={20} />
                                        </span>
                                    )}
                                    <span className="section_arrow">
                                        {openDrawer === key ? (
                                            <Collapsed width={14} height={14} />
                                        ) : (
                                            <Expand width={14} height={14} />
                                        )}
                                    </span>
                                </div>

                                {openDrawer === key && hasResults && !sectionIsLoading && (
                                    <div className="section_content">
                                        {filteredData.length > 0 ? (
                                            <>
                                                {/* --- ALPHABETICAL GRID (expanded) --- */}
                                                {isExpanded && shouldShowAlphabetical ? (
                                                    <div className="alphabetical_grid_view">
                                                        {/* Alphabetical navigation bar */}
                                                        {renderLetterNav(groupedItems, key)}
                                                        {/* Select All */}
                                                        <label className="checkbox_label select_all_label grid_select_all">
                                                            <input
                                                                className="checkbox_input"
                                                                type="checkbox"
                                                                checked={isAllVisibleSelected}
                                                                onChange={() => handleSelectAll(key, filteredData)}
                                                            />
                                                            <span className="custom-checkbox"></span>
                                                            <span className="checkbox_text">Select all</span>
                                                        </label>
                                                        {/* Alphabetical grid: multiple columns per letter if needed */}
                                                        <div className="letter_column_flow">
                                                            {(() => {
                                                                let orderedLetters = Object.keys(groupedItems);
                                                                let result = [];
                                                                orderedLetters.forEach((letter) => {
                                                                    result.push({ type: "header", letter });
                                                                    groupedItems[letter].forEach(item => {
                                                                        result.push({ type: "item", ...item });
                                                                    });
                                                                });
                                                                return result.map((entry, idx) => {
                                                                    if (entry.type === "header") {
                                                                        return (
                                                                            <div
                                                                                key={`header-${entry.letter}-${idx}`}
                                                                                className="letter_column_flow_header"
                                                                                ref={el => letterRefs.current[entry.letter] = el}
                                                                            >
                                                                                {entry.letter}
                                                                            </div>
                                                                        );
                                                                    } else {
                                                                        return (
                                                                            <label
                                                                                key={entry.value}
                                                                                className={`checkbox_label letter_column_flow_item${entry.is_active === false ? " disabled_checkbox" : ""}`}
                                                                            >
                                                                                <input
                                                                                    className="checkbox_input"
                                                                                    type="checkbox"
                                                                                    checked={tempSelectedFilters[key]?.includes(entry.value) || false}
                                                                                    onChange={() => handleCheckboxChange(key, entry.value)}
                                                                                    disabled={entry.is_active === false}
                                                                                />
                                                                                <span className="custom-checkbox"></span>
                                                                                <span className="checkbox_text" title={hasMoreThanSix ? entry.name : undefined}>{entry.name}</span>
                                                                            </label>
                                                                        );
                                                                    }
                                                                });
                                                            })()}
                                                        </div>
                                                        {shouldShowLessLink && (
                                                            <button
                                                                className="show_less_link"
                                                                onClick={() => handleShowLess(key)}
                                                                type="button"
                                                            >
                                                                Show less
                                                            </button>
                                                        )}
                                                    </div>
                                                ) : null}

                                                {/* --- COLLAPSED STATE for ALPHABETICAL (single column, 6 items) --- */}
                                                {!isExpanded && shouldShowAlphabetical && (
                                                    <>
                                                        <label className="checkbox_label select_all_label">
                                                            <input
                                                                className="checkbox_input"
                                                                type="checkbox"
                                                                checked={isAllVisibleSelected}
                                                                onChange={() => handleSelectAll(key, filteredData)}
                                                            />
                                                            <span className="custom-checkbox"></span>
                                                            <span className="checkbox_text">Select all</span>
                                                        </label>
                                                        {itemsToDisplay.map(
                                                            ({ name, value, is_active }) => (
                                                                <label
                                                                    key={value}
                                                                    className={`checkbox_label${is_active === false ? " disabled_checkbox" : ""}`}
                                                                >
                                                                    <input
                                                                        className="checkbox_input"
                                                                        type="checkbox"
                                                                        checked={tempSelectedFilters[key]?.includes(value) || false}
                                                                        onChange={() => handleCheckboxChange(key, value)}
                                                                        disabled={is_active === false}
                                                                    />
                                                                    <span className="custom-checkbox"></span>
                                                                    <span className="checkbox_text" title={hasMoreThanSix ? name : undefined}>{name}</span>
                                                                </label>
                                                            )
                                                        )}
                                                        {shouldShowMoreLink && (
                                                            <button
                                                                className="show_more_link"
                                                                onClick={() => handleShowMore(key)}
                                                                type="button"
                                                            >
                                                                + {filteredData.length - ITEMS_TO_SHOW} more
                                                            </button>
                                                        )}
                                                    </>
                                                )}

                                                {/* --- NON-ALPHABETICAL: VERTICAL (single column) when not expanded --- */}
                                                {!shouldShowAlphabetical && !isExpanded && (
                                                    <>
                                                        <label className="checkbox_label select_all_label">
                                                            <input
                                                                className="checkbox_input"
                                                                type="checkbox"
                                                                checked={isAllVisibleSelected}
                                                                onChange={() => handleSelectAll(key, filteredData)}
                                                            />
                                                            <span className="custom-checkbox"></span>
                                                            <span className="checkbox_text">Select all</span>
                                                        </label>
                                                        {itemsToDisplay.map(
                                                            ({ name, value, is_active }) => (
                                                                <label
                                                                    key={value}
                                                                    className={`checkbox_label${is_active === false ? " disabled_checkbox" : ""}`}
                                                                >
                                                                    <input
                                                                        className="checkbox_input"
                                                                        type="checkbox"
                                                                        checked={tempSelectedFilters[key]?.includes(value) || false}
                                                                        onChange={() => handleCheckboxChange(key, value)}
                                                                        disabled={is_active === false}
                                                                    />
                                                                    <span className="custom-checkbox"></span>
                                                                    <span className="checkbox_text" title={hasMoreThanSix ? name : undefined}>{name}</span>
                                                                </label>
                                                            )
                                                        )}
                                                        {shouldShowMoreLink && (
                                                            <button
                                                                className="show_more_link"
                                                                onClick={() => handleShowMore(key)}
                                                                type="button"
                                                            >
                                                                + {filteredData.length - ITEMS_TO_SHOW} more
                                                            </button>
                                                        )}
                                                    </>
                                                )}

                                                {/* --- NON-ALPHABETICAL: TWO COLUMNS when expanded --- */}
                                                {isExpanded && !shouldShowAlphabetical && (
                                                    <>
                                                        <label className="checkbox_label select_all_label">
                                                            <input
                                                                className="checkbox_input"
                                                                type="checkbox"
                                                                checked={isAllVisibleSelected}
                                                                onChange={() => handleSelectAll(key, filteredData)}
                                                            />
                                                            <span className="custom-checkbox"></span>
                                                            <span className="checkbox_text">Select all</span>
                                                        </label>
                                                        <div className="checkbox_2col">
                                                            <div className="col">
                                                                {leftCol.map(
                                                                    ({ name, value, is_active }) => (
                                                                        <label
                                                                            key={value}
                                                                            className={`checkbox_label${is_active === false
                                                                                ? " disabled_checkbox"
                                                                                : ""
                                                                                }`}
                                                                        >
                                                                            <input
                                                                                className="checkbox_input"
                                                                                type="checkbox"
                                                                                checked={tempSelectedFilters[key]?.includes(value) || false}
                                                                                onChange={() => handleCheckboxChange(key, value)}
                                                                                disabled={is_active === false}
                                                                            />
                                                                            <span className="custom-checkbox"></span>
                                                                            <span className="checkbox_text" title={hasMoreThanSix ? name : undefined}>
                                                                                {name}
                                                                            </span>
                                                                        </label>
                                                                    )
                                                                )}
                                                            </div>
                                                            <div className="col">
                                                                {rightCol.map(
                                                                    ({ name, value, is_active }) => (
                                                                        <label
                                                                            key={value}
                                                                            className={`checkbox_label${is_active === false
                                                                                ? " disabled_checkbox"
                                                                                : ""
                                                                                }`}
                                                                        >
                                                                            <input
                                                                                className="checkbox_input"
                                                                                type="checkbox"
                                                                                checked={tempSelectedFilters[key]?.includes(value) || false}
                                                                                onChange={() => handleCheckboxChange(key, value)}
                                                                                disabled={is_active === false}
                                                                            />
                                                                            <span className="custom-checkbox"></span>
                                                                            <span className="checkbox_text" title={hasMoreThanSix ? name : undefined}>
                                                                                {name}
                                                                            </span>
                                                                        </label>
                                                                    )
                                                                )}
                                                            </div>
                                                        </div>
                                                        {shouldShowLessLink && (
                                                            <button
                                                                className="show_less_link"
                                                                onClick={() => handleShowLess(key)}
                                                                type="button"
                                                            >
                                                                Show less
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            <p className="no_results">No results found</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="button_container">
                    <Button
                        type="button"
                        className="clear_btn"
                        handleClick={handleClearFilters}
                    >
                        Clear All
                    </Button>
                    <Button
                        type="button"
                        className="apply_btn"
                        handleClick={handleApplyFilters}
                    >
                        Apply
                    </Button>
                </div>
            </div>
            {/* Error Modal */}
            <ConfirmationModal
                open={errorModalOpen}
                onClose={() => setErrorModalOpen(false)}
                type="error"
                message={errorModalMsg}
                title="Error"
            />
            <ConfirmationModal
                open={clearAllModalOpen}
                onClose={cancelClearAll}
                onConfirm={confirmClearAll}
                filterName={null}
                title="Warning"
                message="Are you sure you want to clear all filters?"
                confirmText="Clear All"
                cancelText="Cancel"
            />
        </Wrapper>
    );
};

export default CrossGlobalFilter;